import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Card,
  Form,
  InputNumber,
  Button,
  message,
  Row,
  Col,
  Spin,
  Alert,
  Tabs
} from 'antd';
import {
  SaveOutlined
} from '@ant-design/icons';
import { getSystem } from '../../graphql/queries';
import { updateSystem } from '../../graphql/mutation';
import './System.css';

const { TabPane } = Tabs;

const SystemPage = () => {
  const [text2AudioConfig, setText2AudioConfig] = useState(null);
  const [vocal2AudioConfig, setVocal2AudioConfig] = useState(null);

  const [updateSystemMutation] = useMutation(updateSystem);

  // Fetch text2Audio config
  const { data: text2AudioData, loading: text2AudioLoading, refetch: refetchText2Audio } = useQuery(getSystem, {
    variables: { systemType: 'text2Audio' }
  });

  // Fetch vocal2Audio config
  const { data: vocal2AudioData, loading: vocal2AudioLoading, refetch: refetchVocal2Audio } = useQuery(getSystem, {
    variables: { systemType: 'vocal2Audio' }
  });

  useEffect(() => {
    if (text2AudioData?.getSystem) {
      setText2AudioConfig(text2AudioData.getSystem);
    }
  }, [text2AudioData]);

  useEffect(() => {
    if (vocal2AudioData?.getSystem) {
      setVocal2AudioConfig(vocal2AudioData.getSystem);
    }
  }, [vocal2AudioData]);

  const handleSystemUpdate = async (systemId, systemType, creditCost) => {
    try {
      await updateSystemMutation({
        variables: {
          id: systemId,
          systemType: systemType,
          creditCost: creditCost
        }
      });
      message.success('System configuration updated successfully!');

      // Refetch data
      if (systemType === 'text2Audio') {
        refetchText2Audio();
      } else if (systemType === 'vocal2Audio') {
        refetchVocal2Audio();
      }
    } catch (error) {
      message.error('Update failed: ' + error.message);
    }
  };

  if (text2AudioLoading || vocal2AudioLoading) {
    return (
      <div className="system-container">
        <Card>
          <div style={{ textAlign: 'center', padding: '100px' }}>
            <Spin size="large" />
            <p style={{ marginTop: 16 }}>Loading configuration...</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="system-container">
      <Card>
        <Alert
          message="System Configuration"
          description="Configure credit costs for different generation types."
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
          closable
        />

        <Tabs defaultActiveKey="text2Audio">
          {/* Text to Audio Config */}
          <TabPane tab="Text to Audio" key="text2Audio">
            {text2AudioConfig ? (
              <Card title="Text to Audio Generation Settings">
                <Form layout="vertical">
                  <Row gutter={[24, 24]}>
                    <Col xs={24} sm={12}>
                      <Form.Item label="Credit Cost per Generation">
                        <InputNumber
                          value={text2AudioConfig.creditCost}
                          onChange={(value) => setText2AudioConfig({ ...text2AudioConfig, creditCost: value })}
                          min={0}
                          style={{ width: '100%', maxWidth: 200 }}
                          addonAfter="credits"
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Form>
                <div style={{ marginTop: 24 }}>
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    onClick={() => handleSystemUpdate(text2AudioConfig.id, 'text2Audio', text2AudioConfig.creditCost)}
                  >
                    Save Text to Audio Settings
                  </Button>
                </div>
              </Card>
            ) : (
              <Alert message="No configuration found for Text to Audio" type="warning" />
            )}
          </TabPane>

          {/* Vocal to Audio Config */}
          <TabPane tab="Vocal to Audio" key="vocal2Audio">
            {vocal2AudioConfig ? (
              <Card title="Vocal to Audio Generation Settings">
                <Form layout="vertical">
                  <Row gutter={[24, 24]}>
                    <Col xs={24} sm={12}>
                      <Form.Item label="Credit Cost per Generation">
                        <InputNumber
                          value={vocal2AudioConfig.creditCost}
                          onChange={(value) => setVocal2AudioConfig({ ...vocal2AudioConfig, creditCost: value })}
                          min={0}
                          style={{ width: '100%', maxWidth: 200 }}
                          addonAfter="credits"
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Form>
                <div style={{ marginTop: 24 }}>
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    onClick={() => handleSystemUpdate(vocal2AudioConfig.id, 'vocal2Audio', vocal2AudioConfig.creditCost)}
                  >
                    Save Vocal to Audio Settings
                  </Button>
                </div>
              </Card>
            ) : (
              <Alert message="No configuration found for Vocal to Audio" type="warning" />
            )}
          </TabPane>
        </Tabs>
      </Card>
    </div>
  );
};

export default SystemPage;